import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { editionDocumentationService } from "../services/editionDocumentationService";
import { editionDocumentationDisplayId } from "./EditionDocumentationCommonStore";
import { EditionDocumentation } from "../../../api/graphql";
import { musicalWorkDisplayId } from "../../musicalWork/store/MusicalWorkCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { musicalWorkService } from "../../musicalWork/services/musicalWorkService";
import { InitializeParams } from "../../../store/types";

export class EditionDocumentationViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = editionDocumentationService;
  editionDocumentationInstance: EditionDocumentation | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  worksService = musicalWorkService;
  worksSearchValue: string = "";
  worksIdValue: string = "";
  worksSelectedValues: { displayId: string; id: string }[] = [];
  authorsDisplayValue: string = "";
  publisherConfigurationDisplayValue: string = "";
  territoriesDisplayValue: string = "";
  contractDurationValue: number | undefined = undefined;
  contractRenewalValue: number | undefined = undefined;
  contractNumberValue: string = "";
  documentNameValue: string = "";
  finalDocumentPdfFiles: CrudRows = {};
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEditionDocumentationById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.editionDocumentationOne({ id });
    this.editionDocumentationInstance = result.editionDocumentationOne;
    if (this.editionDocumentationInstance) {
      this.loadValues(this.editionDocumentationInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.editionDocumentationInstance) {
      return editionDocumentationDisplayId(
        this.editionDocumentationInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EditionDocumentationSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "works") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.musicalWorkOne({
          id: params.returnData.resultId,
        });
        const musicalWork = result.musicalWorkOne;
        this.worksSelectedValues.push(musicalWork);
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get worksValues() {
    return this.worksSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: musicalWorkDisplayId(x, this.langStore),
      };
    });
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "EditionDocumentation",
      fieldName: fieldName,
      instanceId: this.editionDocumentationInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "EditionDocumentation",
        fieldName: fieldName,
        instanceId: this.editionDocumentationInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.editionDocumentationInstance = null;
    this.worksSearchValue = "";
    this.worksIdValue = "";
    this.worksSelectedValues = [];
    this.authorsDisplayValue = "";
    this.publisherConfigurationDisplayValue = "";
    this.territoriesDisplayValue = "";
    this.contractDurationValue = undefined;
    this.contractRenewalValue = undefined;
    this.contractNumberValue = "";
    this.documentNameValue = "";
    this.finalDocumentPdfFiles = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEditionDocumentation() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editEditionDocumentation() {
    const id = this.editionDocumentationInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/editionDocumentation/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEditionDocumentation(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEditionDocumentation(): any {
    yield this.close();
  }

  loadValues(instance: EditionDocumentation) {
    this.idValue = instance.id;
    this.worksSelectedValues = instance.works ? instance.works : [];
    this.authorsDisplayValue = instance.authorsDisplay || "";
    this.publisherConfigurationDisplayValue =
      instance.publisherConfigurationDisplay || "";
    this.territoriesDisplayValue = instance.territoriesDisplay || "";
    this.contractDurationValue = instance.contractDuration || undefined;
    this.contractRenewalValue = instance.contractRenewal || undefined;
    this.contractNumberValue = instance.contractNumber;
    this.documentNameValue = instance.documentName || "";
    this.finalDocumentPdfFiles = instance.finalDocumentPdf_data
      ? {
          [instance.finalDocumentPdf_data.name]: {
            name: instance.finalDocumentPdf_data.name,
            formattedSize: formatBytes(
              instance.finalDocumentPdf_data?.size || 0
            ),
            mimetype: instance.finalDocumentPdf_data?.mimetype,
            id: instance.finalDocumentPdf_data.name,
            __status__: "original",
          },
        }
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEditionDocumentationById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
